import   React                          ,
       { useState                       ,
         useEffect                      }  from 'react';

import { AddressBookContact     ,
         ContactChannel        ,
         Recipient             ,
         contactValueFor       ,
         recipientFromContact  }  from '../../../dataTypes/adminManagment/types_addressBook';

import { isValidWhatsappNumber }  from '../../../utils/utils_whatsapp';

import { isValidEmail             ,
         RECIPIENT_ICONS          ,
         RECIPIENT_ICON_COLORS    ,
         RECIPIENT_CHANNEL_LABELS }  from '../../../utils/utils_recipients';

import   Icon                              from '../../widget/images/Icon';

import { getAddressBook                 ,
         addAddressBookContact          ,
         updateAddressBookContact       ,
         deleteAddressBookContact       }  from '../../../backendServices/adminManagment/api_addressBook';

import   Box                               from '../../widget/windows/Box';
import   Trigger                           from '../../widget/actuators/Trigger';
import   Field                             from '../../widget/inputs/Field';

import   styles                            from './AddressBookModal.module.scss';
import   classNames                        from 'classnames';

// ─────────────────────────────────────────────────────────────────────────────

interface AddressBookModalProps {
  userId            : number;
  onClose           : () => void;
  // Destinataires cochés, canal compris et nom du contact inclus : chaque
  // coordonnée a sa propre case, et une même personne peut donc être retenue par
  // mail, par WhatsApp, ou les deux.
  onSelectRecipients: (recipients: Recipient[]) => void;
  // Canaux proposés à la sélection. Par défaut les deux ; un appelant qui ne sait
  // envoyer que des mails (l'invitation à une chaîne) restreint la liste plutôt
  // que d'afficher des cases qui ne mèneraient nulle part.
  allowedChannels?  : ContactChannel[];
  minWidth?         : string;
  minHeight?        : string;
}

/** Ordre d'affichage des coordonnées, stable d'un contact à l'autre. */
const CANAUX: ContactChannel[] = ['email', 'whatsapp'];

const AddressBookModal = ({ userId                                    ,
                            onClose                                   ,
                            onSelectRecipients                        ,
                            allowedChannels = CANAUX                  ,
                            minWidth                                  ,
                            minHeight                                 }: AddressBookModalProps) => {

  const [ contacts     , setContacts     ] = useState<AddressBookContact[]>([]);
  const [ loading      , setLoading      ] = useState(true);
  const [ error        , setError        ] = useState<string | null>(null);
  // La sélection porte sur des COORDONNÉES, pas sur des contacts : la clé associe
  // l'entité et le canal, sans quoi cocher le mail d'Alice cocherait aussi son
  // WhatsApp.
  const [ selectedKeys , setSelectedKeys ] = useState<Set<string>>(new Set());

  // ── Formulaire d'ajout (en haut) ──
  const [ newFirstname , setNewFirstname ] = useState('');
  const [ newLastname  , setNewLastname  ] = useState('');
  const [ newEmail     , setNewEmail     ] = useState('');
  const [ newWhatsapp  , setNewWhatsapp  ] = useState('');
  const [ addError     , setAddError     ] = useState<string | null>(null);
  const [ addLoading   , setAddLoading   ] = useState(false);

  // ── Édition en ligne (le crayon d'une ligne de contact) ──
  //
  // Un seul contact en édition à la fois : la ligne éditée remplace son affichage
  // par ses quatre champs. Les valeurs sont recopiées à l'ouverture, pas liées au
  // contact — abandonner l'édition ne doit rien laisser derrière.
  const [ editingId    , setEditingId    ] = useState<number | null>(null);
  const [ editForm     , setEditForm     ] = useState({ firstname: '', lastname: '', email: '', whatsapp: '' });
  const [ editError    , setEditError    ] = useState<string | null>(null);
  const [ editSaving   , setEditSaving   ] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchContacts();
  }, [userId]);

  const fetchContacts = async () => {

    setLoading(true);
    setError  (null);

    const result = await getAddressBook(userId);

    if (result.code === 1) { setContacts(result.payload); }
    else                   { setError('Impossible de charger le carnet d\'adresses.'); }

    setLoading(false);
  };

  // ─────────────────────────────────────────────────────────────────────────

  const handleStartEdit = (contact: AddressBookContact) => {
    setEditingId(contact.entityId);
    setEditForm({
      firstname: contact.firstname ?? '' ,
      lastname : contact.lastname  ?? '' ,
      email    : contact.email     ?? '' ,
      whatsapp : contact.whatsapp  ?? '' ,
    });
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  /**
   * Enregistrement d'un contact modifié.
   *
   * Les quatre champs partent toujours, y compris vides : c'est ainsi qu'on
   * efface une coordonnée devenue fausse. Le serveur distingue « champ absent »
   * (inchangé) de « champ présent et vide » (effacé) — sans quoi une adresse
   * erronée resterait dans le carnet pour toujours.
   */
  const handleSaveEdit = async (entityId: number) => {

    const { firstname, lastname, email, whatsapp } = editForm;

    if (!firstname.trim()) { setEditError('Le prénom est obligatoire.'); return; }
    if (!lastname.trim() ) { setEditError('Le nom est obligatoire.'   ); return; }

    if (!email.trim() && !whatsapp.trim())
    {
      setEditError('Renseignez au moins une adresse email ou un numéro WhatsApp.');
      return;
    }

    if (email.trim() && !isValidEmail(email))
    {
      setEditError('L\'adresse email n\'est pas valide.');
      return;
    }

    if (whatsapp.trim() && !isValidWhatsappNumber(whatsapp))
    {
      setEditError('Le numéro WhatsApp n\'est pas exploitable (ex : +33 6 12 34 56 78).');
      return;
    }

    setEditSaving(true);
    setEditError (null);

    const result = await updateAddressBookContact(userId, entityId, firstname, lastname, email, whatsapp);

    if (result.code === 1)
    {
      // Une coordonnée effacée peut avoir été cochée juste avant : la sélection
      // doit la relâcher, sinon on expédierait vers une adresse qui n'existe plus.
      setSelectedKeys(prev => {
        const next = new Set(prev);
        if (!email.trim()   ) { next.delete(selectionKey(entityId, 'email'   )); }
        if (!whatsapp.trim()) { next.delete(selectionKey(entityId, 'whatsapp')); }
        return next;
      });

      setEditingId(null);
      await fetchContacts();
    }
    else
    {
      setEditError('Erreur lors de la modification du contact.');
    }

    setEditSaving(false);
  };

  const selectionKey = (entityId: number, channel: ContactChannel) => `${entityId}:${channel}`;

  const handleToggleSelect = (entityId: number, channel: ContactChannel) => {

    const cle = selectionKey(entityId, channel);

    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(cle)) { next.delete(cle); }
      else               { next.add   (cle); }
      return next;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────

  const handleDelete = async (entityId: number) => {

    const result = await deleteAddressBookContact(userId, entityId);

    if (result.code === 1)
    {
      setContacts    (prev => prev.filter(c => c.entityId !== entityId));
      setSelectedKeys(prev => {
        const next = new Set(prev);
        CANAUX.forEach(canal => next.delete(selectionKey(entityId, canal)));
        return next;
      });
    }
    else
    {
      setError('Erreur lors de la suppression du contact.');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  const handleAdd = async () => {

    // Les deux canaux sont facultatifs, mais pas les deux à la fois : un contact
    // sans mail ni WhatsApp n'est joignable par personne, et la case à cocher
    // resterait grise à jamais dans les deux modes de sélection.
    if (!newFirstname.trim()) { setAddError('Le prénom est obligatoire.'); return; }
    if (!newLastname.trim() ) { setAddError('Le nom est obligatoire.'   ); return; }

    if (!newEmail.trim() && !newWhatsapp.trim())
    {
      setAddError('Renseignez au moins une adresse email ou un numéro WhatsApp.');
      return;
    }

    if (newEmail.trim() && !isValidEmail(newEmail))
    {
      setAddError('L\'adresse email n\'est pas valide.');
      return;
    }

    if (newWhatsapp.trim() && !isValidWhatsappNumber(newWhatsapp))
    {
      setAddError('Le numéro WhatsApp n\'est pas exploitable (ex : +33 6 12 34 56 78).');
      return;
    }

    setAddLoading(true);
    setAddError  (null);

    const result = await addAddressBookContact(
      userId      ,
      'Person'    ,
      newFirstname,
      newLastname ,
      newEmail    ,
      newWhatsapp ,
    );

    if (result.code === 1)
    {
      setNewFirstname('');
      setNewLastname ('');
      setNewEmail    ('');
      setNewWhatsapp ('');
      await fetchContacts();
    }
    else
    {
      setAddError('Erreur lors de l\'ajout du contact.');
    }

    setAddLoading(false);
  };

  // ─────────────────────────────────────────────────────────────────────────

  /** Le contact est-il joignable sur ce canal ? */
  const estJoignable = (contact: AddressBookContact, channel: ContactChannel): boolean => {
    const valeur = contactValueFor(contact, channel);
    return valeur !== null && valeur !== '';
  };

  /** Canaux effectivement proposés pour ce contact : ceux qu'il porte, et qu'on accepte. */
  const canauxDisponibles = (contact: AddressBookContact): ContactChannel[] =>
    CANAUX.filter(canal => allowedChannels.includes(canal) && estJoignable(contact, canal));

  const handleConfirmSelection = () => {

    // L'ordre suit la liste affichée, pas l'ordre des clics : c'est celui que
    // l'opérateur vient de lire.
    const recipients: Recipient[] = [];

    contacts.forEach(contact => {
      canauxDisponibles(contact).forEach(canal => {
        if (!selectedKeys.has(selectionKey(contact.entityId, canal))) { return; }
        const destinataire = recipientFromContact(contact, canal);
        if (destinataire !== null) { recipients.push(destinataire); }
      });
    });

    onSelectRecipients(recipients);
    onClose();
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box onClose={onClose} width="640px" maxHeight="90vh" minWidth={minWidth} minHeight={minHeight} marginTop="0px" contentPadding="20px" transparentOverlay solidBackground>

      <p className={classNames(styles.title)}>Carnet d'adresses</p>

        <div className={classNames(styles.body)}>

          {/* ── Formulaire d'ajout ── */}
          <div className={classNames(styles.addForm)}>

            <div className={classNames(styles.addFormRow)}>

              <div className={classNames(styles.addFormField)}>
                <label className={classNames(styles.addFormLabel)}>Prénom</label>
                <Field type        = "text"
                       value       = {newFirstname}
                       placeholder = "Prénom"
                       onChange    = {(e) => setNewFirstname(e.target.value)} />
              </div>

              <div className={classNames(styles.addFormField)}>
                <label className={classNames(styles.addFormLabel)}>Nom</label>
                <Field type        = "text"
                       value       = {newLastname}
                       placeholder = "Nom"
                       onChange    = {(e) => setNewLastname(e.target.value)} />
              </div>

              <div className={classNames(styles.addFormField, styles.addFormFieldEmail)}>
                <label className={classNames(styles.addFormLabel)}>Email</label>
                <Field type        = "text"
                       value       = {newEmail}
                       placeholder = "email@exemple.com"
                       onChange    = {(e) => setNewEmail(e.target.value)} />
              </div>

              {/* Identifiant WhatsApp = numéro de téléphone. L'indicatif est ajouté
                  tout seul si le numéro est saisi au format national. */}
              <div className={classNames(styles.addFormField, styles.addFormFieldEmail)}>
                <label className={classNames(styles.addFormLabel)}>WhatsApp</label>
                <Field type        = "text"
                       value       = {newWhatsapp}
                       placeholder = "+33 6 12 34 56 78"
                       onChange    = {(e) => setNewWhatsapp(e.target.value)} />
              </div>

              <div className={classNames(styles.addFormButton)}>
                <Trigger name                       = {addLoading ? '...' : 'Ajouter'}
                         onClick                    = {handleAdd}
                         variant                    = "tailwind2"
                         buttonForegroundColor      = "#FFFFFF"
                         buttonBackgroundColor      = "#2563EB"
                         buttonForegroundHoverColor = "#FFFFFF"
                         buttonBackgroundHoverColor = "#1D4ED8" />
              </div>

            </div>

            {
              addError &&
              <div className={classNames(styles.errorMessage)}>{addError}</div>
            }

          </div>

          {/* ── Séparateur ── */}
          <div className={classNames(styles.separator)} />

          {/* ── Liste des contacts ── */}
          {
            error &&
            <div className={classNames(styles.errorMessage)}>{error}</div>
          }

          {
            loading
            ? <div className={classNames(styles.loadingMessage)}>Chargement...</div>
            : contacts.length === 0
              ? <div className={classNames(styles.emptyMessage)}>Le carnet d'adresses est vide.</div>
              : (
                  <div className={classNames(styles.contactList)}>
                    { contacts.map(contact => (

                        <div key       = {contact.entityId}
                             className = {classNames(
                                            styles.contactRow,
                                            canauxDisponibles(contact).some(c => selectedKeys.has(selectionKey(contact.entityId, c)))
                                            && styles.contactRowSelected
                                          )}>

                          {
                            editingId === contact.entityId

                            /* ── Édition : les quatre informations deviennent des
                                  champs de saisie, à la place de leur affichage. ── */
                            ? <div className={classNames(styles.contactEditForm)}>

                                <div className={classNames(styles.contactEditRow)}>
                                  <Field type        = "text"
                                         value       = {editForm.firstname}
                                         placeholder = "Prénom"
                                         onChange    = {(e) => setEditForm(f => ({ ...f, firstname: e.target.value }))} />
                                  <Field type        = "text"
                                         value       = {editForm.lastname}
                                         placeholder = "Nom"
                                         onChange    = {(e) => setEditForm(f => ({ ...f, lastname: e.target.value }))} />
                                </div>

                                <div className={classNames(styles.contactEditRow)}>
                                  <span className={classNames(styles.contactChannelIcon)}>
                                    <Icon name      = {RECIPIENT_ICONS.email}
                                          width     = {16}
                                          height    = {16}
                                          color     = {RECIPIENT_ICON_COLORS.email}
                                          hoverable = {false}
                                          clickable = {false} />
                                  </span>
                                  <Field type        = "text"
                                         value       = {editForm.email}
                                         placeholder = "email@exemple.com"
                                         onChange    = {(e) => setEditForm(f => ({ ...f, email: e.target.value }))} />
                                </div>

                                <div className={classNames(styles.contactEditRow)}>
                                  <span className={classNames(styles.contactChannelIcon)}>
                                    <Icon name      = {RECIPIENT_ICONS.whatsapp}
                                          width     = {16}
                                          height    = {16}
                                          color     = {RECIPIENT_ICON_COLORS.whatsapp}
                                          hoverable = {false}
                                          clickable = {false} />
                                  </span>
                                  <Field type        = "text"
                                         value       = {editForm.whatsapp}
                                         placeholder = "+33 6 12 34 56 78"
                                         onChange    = {(e) => setEditForm(f => ({ ...f, whatsapp: e.target.value }))} />
                                </div>

                                { editError && <div className={classNames(styles.errorMessage)}>{editError}</div> }

                                <div className={classNames(styles.contactEditActions)}>
                                  <Trigger name    = "Annuler"
                                           onClick = {handleCancelEdit}
                                           variant = "tailwind2" />
                                  <Trigger name                       = {editSaving ? '...' : 'Enregistrer'}
                                           onClick                    = {() => handleSaveEdit(contact.entityId)}
                                           variant                    = "tailwind2"
                                           buttonForegroundColor      = "#FFFFFF"
                                           buttonBackgroundColor      = "#2563EB"
                                           buttonForegroundHoverColor = "#FFFFFF"
                                           buttonBackgroundHoverColor = "#1D4ED8" />
                                </div>

                              </div>

                            /* ── Affichage : une case PAR COORDONNÉE. Le mail et le
                                  WhatsApp d'une même personne se cochent séparément et
                                  se retrouvent tous deux dans la zone d'envoi si on le
                                  veut. Une coordonnée absente n'affiche pas de case —
                                  il n'y a rien à cocher, seulement à constater. ── */
                            : <div className={classNames(styles.contactInfos)}>

                                <span className={classNames(styles.contactName)}>
                                  {contact.firstname} {contact.lastname}
                                </span>

                                { CANAUX.map(canal => {

                                    const valeur    = contactValueFor(contact, canal);
                                    const propose   = allowedChannels.includes(canal);
                                    const joignable = valeur !== null && valeur !== '';
                                    const cle       = selectionKey(contact.entityId, canal);

                                    if (!propose && !joignable) { return null; }

                                    return (
                                      <label key       = {canal}
                                             className = {classNames(styles.contactChannelLine, !joignable && styles.contactChannelSecondary)}
                                             title     = {`${RECIPIENT_CHANNEL_LABELS[canal]}${joignable ? ' — ' + valeur : ' non renseigné'}`}>

                                        { propose && joignable
                                          ? <input type      = "checkbox"
                                                   checked   = {selectedKeys.has(cle)}
                                                   onChange  = {() => handleToggleSelect(contact.entityId, canal)}
                                                   className = {classNames(styles.contactCheckbox)} />
                                          : <span className={classNames(styles.contactCheckboxPlaceholder)} />
                                        }

                                        {/* Le pictogramme seul dit le canal : enveloppe pour
                                            le mail, combiné en vert WhatsApp pour l'autre.
                                            L'infobulle porte le mot pour qui en doute. */}
                                        <span className={classNames(styles.contactChannelIcon)}>
                                          <Icon name      = {RECIPIENT_ICONS[canal]}
                                                width     = {16}
                                                height    = {16}
                                                color     = {RECIPIENT_ICON_COLORS[canal]}
                                                hoverable = {false}
                                                clickable = {false} />
                                        </span>

                                        <span className={classNames(styles.contactEmail)}>
                                          {joignable ? valeur : <em>Non renseigné</em>}
                                        </span>

                                      </label>
                                    );
                                  })
                                }

                              </div>
                          }

                          {
                            editingId !== contact.entityId &&
                            <div className={classNames(styles.contactActions)}>
                              <Trigger iconName   = "Pencil"
                                       iconWidth  = {14}
                                       iconHeight = {14}
                                       onClick    = {() => handleStartEdit(contact)} />
                              <Trigger iconName   = "Trash2"
                                       iconWidth  = {14}
                                       iconHeight = {14}
                                       onClick    = {() => handleDelete(contact.entityId)} />
                            </div>
                          }

                        </div>
                      ))
                    }
                  </div>
                )
          }

          {/* ── Footer ── */}
          <div className={classNames(styles.footer)}>
            <Trigger name    = "Annuler"
                     onClick = {onClose}
                     variant = "tailwind2" />
            <Trigger name                       = {`Ajouter ${selectedKeys.size > 0 ? `(${selectedKeys.size})` : ''} à la liste`}
                     onClick                    = {handleConfirmSelection}
                     variant                    = "tailwind2"
                     buttonForegroundColor      = {selectedKeys.size > 0 ? '#FFFFFF' : undefined}
                     buttonBackgroundColor      = {selectedKeys.size > 0 ? '#059669' : undefined}
                     buttonForegroundHoverColor = {selectedKeys.size > 0 ? '#FFFFFF' : undefined}
                     buttonBackgroundHoverColor = {selectedKeys.size > 0 ? '#047857' : undefined} />
          </div>

        </div>
    </Box>
  );
};

export default AddressBookModal;